import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { triggerBrowserDownload } from '../utils/download';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Grid,
  Alert,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Pagination
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  BarChart as BarChartIcon,
  Logout as LogoutIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  HelpOutline as HelpOutlineIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import axios from 'axios';
import { APP_NAME } from '../constants';
import RajidaLogo from '../imgs/Rajida-New-Logo.png';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const token = localStorage.getItem('token');
  
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalStorage: 0,
    usedStorage: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [view, setView] = useState('overview'); // 'overview', 'users', 'sql-backups', 'settings'
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    storage_quota: 1073741824, // 1GB in bytes
    is_active: true
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(20);
  const [sqlBackupUsers, setSqlBackupUsers] = useState([]);
  const [sqlBackupUsersLoading, setSqlBackupUsersLoading] = useState(false);
  const [selectedSqlBackupUser, setSelectedSqlBackupUser] = useState(null);
  const [userSqlBackupFiles, setUserSqlBackupFiles] = useState([]);
  const [userSqlBackupFilesLoading, setUserSqlBackupFilesLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [sqlBackupUsersPage, setSqlBackupUsersPage] = useState(1);
  const [sqlBackupUsersPerPage, setSqlBackupUsersPerPage] = useState(20);

  useEffect(() => {
    if (user && token) {
      // Check if token is still valid by making a simple request
      const checkTokenValidity = async () => {
        try {
          await axios.get('/drive/api/admin/config', {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Token is valid, fetch data
          fetchUsers();
          fetchSystemStats();
          fetchAdminConfig();
        } catch (error) {
          if (error.response?.status === 401) {
            setError('Session expired. Please log in again.');
            setTimeout(() => {
              logout();
              navigate('/login');
            }, 2000);
          }
        }
      };
      
      checkTokenValidity();
    }
  }, [user, token]);

  useEffect(() => {
    if (user && token && view === 'sql-backups') {
      fetchSqlBackupUsers();
    }
  }, [view, user, token]);

  useEffect(() => {
    if (view === 'sql-backups') {
      setSqlBackupUsersPage(1); // Reset to first page when search query changes
    }
  }, [userSearchQuery, view]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get('/drive/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        setError('Failed to fetch users');
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchSystemStats = async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get('/drive/api/admin/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemStats(response.data);
    } catch (error) {
      console.error('Failed to fetch system stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAdminConfig = async () => {
    try {
      const response = await axios.get('/drive/api/admin/config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsersPerPage(response.data.users_per_page);
      setSqlBackupUsersPerPage(response.data.users_per_page);
    } catch (error) {
      console.error('Failed to fetch admin config:', error);
      // Keep default value if config fetch fails
    }
  };

  const fetchSqlBackupUsers = async () => {
    setSqlBackupUsersLoading(true);
    setSqlBackupUsersPage(1); // Reset to first page when refreshing
    try {
      const response = await axios.get('/drive/api/admin/sql-backup-users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSqlBackupUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch SQL backup users:', error);
      setError('Failed to fetch SQL backup users');
    } finally {
      setSqlBackupUsersLoading(false);
    }
  };

  const fetchUserSqlBackupFiles = async (userId) => {
    setUserSqlBackupFilesLoading(true);
    try {
      const response = await axios.get(`/drive/api/admin/sql-backup-files/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSqlBackupFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to fetch user SQL backup files:', error);
      setError('Failed to fetch SQL backup files');
    } finally {
      setUserSqlBackupFilesLoading(false);
    }
  };

  const handleDownloadSqlBackup = async (file) => {
    try {
      // Stream via the browser's native download manager (real progress UI,
      // streams to disk instead of buffering the file in memory).
      // Uploads backups live in a dedicated subfolder, so they use their own
      // download-token type; database backups keep using the original one.
      await triggerBrowserDownload({
        type: file.category === 'uploads' ? 'admin_sqlbackup_uploads' : 'admin_sqlbackup',
        filename: file.name,
        userId: selectedSqlBackupUser.id,
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to download SQL backup:', error);
      setError('Failed to download SQL backup file');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateUser = () => {
    setIsEditMode(false);
    setUserForm({
      username: '',
      email: '',
      password: '',
      storage_quota: 1073741824,
      is_active: true
    });
    setSelectedUser(null);
    setUserDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setIsEditMode(true);
    setSelectedUser(user);
    setUserForm({
      username: user.username,
      email: user.email,
      password: '',
      storage_quota: user.storage_quota,
      is_active: user.is_active
    });
    setUserDialogOpen(true);
  };

  const handleUserSubmit = async () => {
    try {
      if (isEditMode) {
        await axios.put(`/drive/api/admin/users/${selectedUser.id}`, userForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('User updated successfully');
      } else {
        await axios.post('/drive/api/admin/users', userForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('User created successfully');
      }
      
      setUserDialogOpen(false);
      setUsersPage(1); // Reset to first page
      fetchUsers();
      fetchSystemStats();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        // Redirect to login after a short delay
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        setError(error.response?.data?.error || 'Operation failed');
      }
    }
  };



  const formatSize = (size) => {
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return (size / 1024).toFixed(2) + ' KB';
    if (size < 1024 * 1024 * 1024) return (size / (1024 * 1024)).toFixed(2) + ' MB';
    return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const renderOverviewContent = () => (
    <Grid container spacing={3}>
      {/* System Statistics Cards */}
      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Total Users</Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {statsLoading ? '...' : systemStats.totalUsers}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registered users
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="h6">Active Users</Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {statsLoading ? '...' : systemStats.activeUsers}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Currently active
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StorageIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">Storage Used</Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
              {statsLoading ? '...' : formatSize(systemStats.usedStorage)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Of {formatSize(systemStats.totalStorage)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BarChartIcon sx={{ mr: 1, color: 'info.main' }} />
              <Typography variant="h6">Usage</Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
              {statsLoading ? '...' : ((systemStats.usedStorage / systemStats.totalStorage) * 100).toFixed(2)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Storage utilization
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Storage Usage Chart */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Storage Usage Overview</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={statsLoading ? 0 : Math.min(100, parseFloat(((systemStats.usedStorage / systemStats.totalStorage) * 100).toFixed(2)))}
                sx={{ height: 10, borderRadius: 5 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Used: {statsLoading ? '...' : formatSize(systemStats.usedStorage)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available: {statsLoading ? '...' : formatSize(systemStats.totalStorage - systemStats.usedStorage)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Actions */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Quick Actions</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateUser}
              >
                Create New User
              </Button>
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => setView('users')}
              >
                Manage Users
              </Button>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setView('settings')}
              >
                System Settings
              </Button>
              <Button
                variant="outlined"
                startIcon={<StorageIcon />}
                onClick={() => setView('sql-backups')}
              >
                SQL Backups
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderUsersContent = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setView('overview')} sx={{ mr: 1 }}>
              <SecurityIcon />
            </IconButton>
            <Typography variant="h6">User Management</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
          >
            Add User
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {usersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <LinearProgress sx={{ width: '50%' }} />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Storage Quota</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const startIndex = (usersPage - 1) * usersPerPage;
                    const endIndex = startIndex + usersPerPage;
                    const paginatedUsers = users.slice(startIndex, endIndex);
                    
                    return paginatedUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 40, height: 40 }}>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {user.username}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {user.id.substring(0, 8)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            color={user.is_active ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatSize(user.storage_quota)}</TableCell>
                        <TableCell>
                          {user.last_login ? formatDate(user.last_login) : 'Never'}
                        </TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        <TableCell>
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            {users.length > usersPerPage && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 3, 
                mb: 1,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Page {usersPage} of {Math.ceil(users.length / usersPerPage)} ({users.length} total users)
                  </Typography>
                  <Pagination
                    count={Math.ceil(users.length / usersPerPage)}
                    page={usersPage}
                    onChange={(event, value) => setUsersPage(value)}
                    size="small"
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  const renderSqlBackupsContent = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => setView('overview')} sx={{ mr: 1 }}>
            <SecurityIcon />
          </IconButton>
          <Typography variant="h6">SQL Backups Management</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {!selectedSqlBackupUser ? (
          // User Selection View
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search users by username or email..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                sx={{ maxWidth: 400 }}
              />
              <Button
                variant="contained"
                onClick={fetchSqlBackupUsers}
                disabled={sqlBackupUsersLoading}
                sx={{ ml: 2 }}
              >
                {sqlBackupUsersLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>

            {sqlBackupUsersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <LinearProgress sx={{ width: '50%' }} />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>SQL Backup Files</TableCell>
                        <TableCell>Total Size</TableCell>
                        <TableCell>Uploads Files</TableCell>
                        <TableCell>Uploads Size</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const filteredUsers = sqlBackupUsers.filter(user => 
                          user.username.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                          user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                        );
                        
                        const startIndex = (sqlBackupUsersPage - 1) * sqlBackupUsersPerPage;
                        const endIndex = startIndex + sqlBackupUsersPerPage;
                        const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
                        
                        return paginatedUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 40, height: 40 }}>
                                  <PersonIcon />
                                </Avatar>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {user.username}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.backup_count || 0}</TableCell>
                            <TableCell>{formatSize(user.backup_size || 0)}</TableCell>
                            <TableCell>{user.uploads_count || 0}</TableCell>
                            <TableCell>{formatSize(user.uploads_size || 0)}</TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setSelectedSqlBackupUser(user);
                                  fetchUserSqlBackupFiles(user.id);
                                }}
                              >
                                View Files
                              </Button>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Pagination for SQL Backup Users */}
                {(() => {
                  const filteredUsers = sqlBackupUsers.filter(user => 
                    user.username.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                    user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                  );
                  const totalPages = Math.ceil(filteredUsers.length / sqlBackupUsersPerPage);
                  
                  return filteredUsers.length > sqlBackupUsersPerPage && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mt: 3, 
                      mb: 1,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Page {sqlBackupUsersPage} of {totalPages} ({filteredUsers.length} total users)
                        </Typography>
                        <Pagination
                          count={Math.max(1, totalPages)}
                          page={sqlBackupUsersPage}
                          onChange={(event, value) => setSqlBackupUsersPage(value)}
                          size="small"
                          color="primary"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    </Box>
                  );
                })()}
              </>
            )}
          </Box>
        ) : (
          // File Listing View
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={() => setSelectedSqlBackupUser(null)} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">
                SQL Backups for {selectedSqlBackupUser.username}
              </Typography>
            </Box>

            {userSqlBackupFilesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <LinearProgress sx={{ width: '50%' }} />
              </Box>
            ) : userSqlBackupFiles.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No SQL backup files found for this user.
              </Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Modified Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userSqlBackupFiles.map((file) => (
                      <TableRow key={file.name}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {file.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={file.category === 'uploads' ? 'secondary' : 'primary'}
                            label={file.category === 'uploads' ? 'Uploads' : 'Database'}
                          />
                        </TableCell>
                        <TableCell>{formatSize(file.size)}</TableCell>
                        <TableCell>{formatDate(file.modified_date)}</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadSqlBackup(file)}
                          >
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderSettingsContent = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => setView('overview')} sx={{ mr: 1 }}>
            <SecurityIcon />
          </IconButton>
          <Typography variant="h6">System Settings</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        <Typography variant="body1" color="text.secondary">
          System settings and configuration options will be available here.
        </Typography>
      </CardContent>
    </Card>
  );

  const menuItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <SecurityIcon />,
      onClick: () => setView('overview')
    },
    {
      id: 'users',
      label: 'Users',
      icon: <PeopleIcon />,
      onClick: () => setView('users')
    },
    {
      id: 'sql-backups',
      label: 'SQL Backups',
      icon: <StorageIcon />,
      onClick: () => setView('sql-backups')
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      onClick: () => setView('settings')
    }
  ];

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      bgcolor: mode === 'dark' ? 'grey.900' : 'grey.50'
    }}>
      {/* App Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img src={RajidaLogo} alt="Rajida Logo" style={{ height: 32, marginRight: 12 }} />
            <Typography variant="h6" component="div">
              Admin Dashboard
            </Typography>
            <Chip
              label="ADMIN"
              color="error"
              size="small"
              sx={{ ml: 2, fontWeight: 'bold' }}
            />
            {user?.username && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'normal',
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  ml: 2,
                  fontSize: '0.9rem',
                }}
              >
                {user.username}
              </Typography>
            )}
          </Box>

          <Button
            href="https://support.rajida.com/"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<HelpOutlineIcon />}
            variant="outlined"
            size="small"
            sx={{ ml: 1, mr: 1, textTransform: 'none', fontWeight: 500 }}
          >
            Support
          </Button>

          <IconButton onClick={toggleTheme} color="inherit">
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Toolbar />

      {/* Navigation Menu */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        py: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        bgcolor: mode === 'dark' ? 'grey.800' : 'grey.100',
        background: mode === 'dark' 
          ? 'linear-gradient(180deg, rgba(66,66,66,0.8) 0%, rgba(48,48,48,0.9) 100%)'
          : 'linear-gradient(180deg, rgba(245,245,245,0.8) 0%, rgba(224,224,224,0.9) 100%)'
      }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {menuItems.map((item) => (
            <Button
              key={item.id}
              variant={view === item.id ? 'contained' : 'outlined'}
              startIcon={item.icon}
              onClick={item.onClick}
              sx={{ textTransform: 'none' }}
            >
              {item.label}
            </Button>
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ textTransform: 'none' }}
          >
            Logout
          </Button>
        </Box>
      </Box>

              {/* Page Content */}
        <Box sx={{ 
          p: 3, 
          flexGrow: 1,
          bgcolor: mode === 'dark' ? 'grey.900' : 'grey.50',
          minHeight: 'calc(100vh - 120px)' // Account for AppBar and navigation
        }}>
          {view === 'overview' ? renderOverviewContent() : 
           view === 'users' ? renderUsersContent() : 
           view === 'sql-backups' ? renderSqlBackupsContent() :
           renderSettingsContent()}
        </Box>

      {/* Footer */}
      <Box sx={{ 
        width: '100%', 
        textAlign: 'center', 
        py: 2, 
        color: 'text.secondary', 
        fontSize: '0.8em', 
        borderTop: 1, 
        borderColor: 'divider', 
        bgcolor: mode === 'dark' ? 'grey.800' : 'grey.100',
        background: mode === 'dark'
          ? 'linear-gradient(180deg, rgba(48,48,48,0.9) 0%, rgba(66,66,66,0.8) 100%)'
          : 'linear-gradient(180deg, rgba(224,224,224,0.9) 0%, rgba(245,245,245,0.8) 100%)'
      }}>
        Copyright © 2025 Rajida Holdings (Pvt) Ltd. All Rights Reserved.
      </Box>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit User' : 'Create New User'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Username"
            value={userForm.username}
            onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
            disabled={isEditMode}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Email"
            type="email"
            value={userForm.email}
            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
          />
          <TextField
            margin="normal"
            required={!isEditMode}
            fullWidth
            label={isEditMode ? 'New Password (leave blank to keep current)' : 'Password'}
            type="password"
            value={userForm.password}
            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Storage Quota (bytes)"
            type="number"
            value={userForm.storage_quota}
            onChange={(e) => setUserForm({ ...userForm, storage_quota: parseInt(e.target.value) })}
            helperText={`${formatSize(userForm.storage_quota)}`}
          />
          <FormControlLabel
            control={
              <Switch
                checked={userForm.is_active}
                onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
              />
            }
            label="User is active"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUserSubmit} variant="contained">
            {isEditMode ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
